import { FunnelStatusName } from 'src/modules/shared/components/atoms/funnel-status-name';
import SearchableSelect from 'src/modules/shared/components/atoms/inputs/searchable-select';
import { Text } from 'src/modules/shared/components/atoms/text';
import type { MappingValue } from 'src/modules/automations/components/molecules/create-trigger-modal/mapping-row';
import type { SemanticQuickAutomationConfigurationProps } from 'src/modules/automations/types/semantic-quick-automation.type';
import { buildSemanticQuickAutomationMapping } from 'src/modules/automations/utils/templates/semantic-quick-automation.util';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';

const readNumberLiteral = (
  mapping: Record<string, MappingValue>,
  field: string
): number | null => {
  if (!Object.prototype.hasOwnProperty.call(mapping, field)) {
    return null;
  }
  const value = mapping[field];
  if (value.kind !== 'literal' || value.literalType !== 'number') {
    return null;
  }

  const parsed = Number(value.value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
};

/**
 * Renders human-readable selectors for strict quick-automation adapters.
 *
 * Status and User labels remain visible while their numeric IDs are written
 * into the existing trigger mapping expected by the workflow.
 */
export const SemanticQuickAutomationConfiguration: React.FC<
  SemanticQuickAutomationConfigurationProps
> = ({
  kind,
  statuses,
  users,
  mapping,
  labels,
  statusesLoading = false,
  usersLoading = false,
  disabled = false,
  onMappingChange,
}) => {
  const selectedStatusId = readNumberLiteral(mapping, 'funnelStatusId');
  const selectedUserId = readNumberLiteral(mapping, 'userId');
  const statusOptions = statuses.map((status) => ({
    value: String(status.id),
    textValue: status.name,
    content: (
      <FunnelStatusName name={status.name} outcome={status.terminalOutcome} />
    ),
  }));
  const userOptions = users.map((user) => {
    const displayName = formatFullName(
      user.firstName,
      user.lastName,
      user.email
    );

    return {
      value: String(user.id),
      content: displayName,
      keywords: `${displayName} ${user.email}`,
    };
  });

  return (
    <div className="space-y-4">
      <label className="block space-y-1">
        <Text as="span" size="tiny" weight="medium">
          {labels.status}
        </Text>
        <SearchableSelect
          value={
            selectedStatusId === null ? undefined : String(selectedStatusId)
          }
          options={statusOptions}
          onChange={(value) => {
            if (value === undefined) {
              return;
            }
            onMappingChange(
              buildSemanticQuickAutomationMapping(
                mapping,
                Number(value),
                selectedUserId
              )
            );
          }}
          loading={statusesLoading}
          disabled={disabled}
          placeholder={labels.statusPlaceholder}
          emptyLabel={labels.emptyStatuses}
        />
      </label>

      {kind === 'status-and-person-user' && (
        <label className="block space-y-1">
          <Text as="span" size="tiny" weight="medium">
            {labels.user}
          </Text>
          <SearchableSelect
            value={selectedUserId === null ? undefined : String(selectedUserId)}
            options={userOptions}
            onChange={(value) => {
              if (value === undefined) {
                return;
              }
              onMappingChange(
                buildSemanticQuickAutomationMapping(
                  mapping,
                  selectedStatusId,
                  Number(value)
                )
              );
            }}
            loading={usersLoading}
            disabled={disabled}
            placeholder={labels.userPlaceholder}
            emptyLabel={labels.emptyUsers}
          />
        </label>
      )}
    </div>
  );
};
